/**
 * Mobile gameplay — 3D highway rhythm game.
 *
 * Renders the SAME visual language as the Flow editor:
 *   • note heads = the editor's Note.fbx mesh + gameplay material (lane colour,
 *     emissive, metalness, roughness, scale) — instanced per lane so a 200-note
 *     chart stays cheap on a phone;
 *   • trails = the editor's animated shader ribbon (moving highlight sweep),
 *     width = gameplaySettings.trailWidth;
 *   • the editor's light rig (ambient + directional + magenta/violet points);
 *   • materials read LIVE from flowStore.gameplaySettings (the user's saved
 *     settings, falling back to the baked defaults).
 *
 * Viewed from the mobile gameplay camera (highway receding to a vanishing
 * point, notes approaching the strike). Self-contained (does NOT mount the
 * editor's FlowScene/store-driven scene) so it can't affect the editor.
 *
 * Hits/sparks/sound fire ONLY on a tap; un-tapped notes pass the strike line
 * silently (miss).
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Line } from '@react-three/drei';
import { mergeBufferGeometries, FBXLoader } from 'three-stdlib';
import * as THREE from 'three';
import * as audio from '../editors/flow/flowAudioEngine';
import { DEFAULT_FLOW_GAMEPLAY, useFlowStore, type FlowGameplaySettings } from '../editors/flow/flowStore';
import { playLaneHit } from '../editors/flow/flowSounds';
import { PlayNote, notesFromDifficulty, laneCountOfDifficulty, PLAY_LANE_COUNT } from './playerNotes';
import { tapHaptic, initHaptics } from './haptics';
import { fetchGlobalDefaults, saveGlobalDefaults } from './playerDefaults';
import { color } from '../components/ui/theme';

const HIT_PERFECT = 0.05;
const HIT_GOOD = 0.11;
// Base note tuning. The phone "Note Speed" slider scales these: the on-screen
// spawn distance (BASE_LEAD·BASE_Z_SPEED) stays constant, so a higher speed
// means notes cover it faster (less reaction time) and vice-versa.
const BASE_LEAD = 2.0;            // seconds of look-ahead at 1× speed
const BASE_Z_SPEED = 12;          // world units / s at 1× speed
const SPAWN_DIST = BASE_LEAD * BASE_Z_SPEED;   // invariant on-screen runway (=24)
const TRACK_W = 4;                // matches editor TUNNEL_WIDTH
// FIXED per-lane width for NOTE / pad SIZING (always TRACK_W/4) so notes & pads
// keep the same size whether the difficulty uses 3 or 4 lanes — only their
// distribution across the track changes (mirrors the editor's LANE_WIDTH).
const LANE_W = TRACK_W / PLAY_LANE_COUNT;
/** X of a lane centre for a given lane count, lanes spread evenly across the
 *  track. lane 0 at +x (screen-left), like the editor. */
const laneXFor = (lane: number, lanes: number) => TRACK_W / 2 - (TRACK_W / lanes) * (lane + 0.5);
const NOTE_LENGTH = 0.45;         // matches editor NOTE_LENGTH (mesh normalise)
const TRAIL_Y = 0.045;            // trail ribbon sits flat on the floor
// Note head sits on (essentially coplanar with) the ribbon. Floating it higher
// caused a perspective parallax shift on the outer lanes that made the head
// look laterally offset from its rail — keep it on the ribbon plane.
const NOTE_Y = 0.06;

const NOTE_FBX_PATH = '/models/Note.fbx';

const clampNum = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

// Stadium / pill OUTLINE as a closed polyline in the XZ plane (lies flat).
// W = width across the lane, H = depth along the track. Drawn with a fixed
// pixel-width line so it reads as a clean outline at any distance.
function pillOutlinePoints(W: number, H: number, segs = 44): [number, number, number][] {
  const r = H / 2, hw = Math.max(0, W / 2 - r);
  const pts: [number, number, number][] = [];
  for (let i = 0; i <= segs; i++) { const a = -Math.PI / 2 + (Math.PI * i) / segs; pts.push([hw + r * Math.cos(a), 0, r * Math.sin(a)]); }
  for (let i = 0; i <= segs; i++) { const a = Math.PI / 2 + (Math.PI * i) / segs; pts.push([-hw + r * Math.cos(a), 0, r * Math.sin(a)]); }
  pts.push(pts[0]);
  return pts;
}

// Camera position for a given track-tilt (degrees of extra pitch from the base
// view). Rotates the camera around the look-at target in the Y–Z plane so the
// strike line stays framed; 0° reproduces the original perspective exactly.
const CAM_TARGET: [number, number, number] = [0, 0.2, 8];
// distExtra (world units) pushes the camera further from / closer to the target
// along the view axis — a zoom out/in.
function cameraPosForTilt(tiltDeg: number, distExtra = 0): [number, number, number] {
  const vy = 3.8, vz = 13.2;                       // base offset behind+above target
  const r = Math.max(3, Math.hypot(vy, vz) + distExtra);
  const phi = Math.atan2(vy, vz) + (tiltDeg * Math.PI) / 180;
  return [0, CAM_TARGET[1] + r * Math.sin(phi), CAM_TARGET[2] - r * Math.cos(phi)];
}

// ── Phone player settings (persisted) ────────────────────────────────────────
// Shares the editor's storage key so the editor's Phone tab and this in-game
// panel stay in sync on the same origin. On a phone (separate origin) it's the
// device's own saved default; QR query params seed it the first time.
export interface PhoneOpts { noteSpeed: number; noteSize: number; trackTilt: number; camHeight: number; camDist: number; }
export type PlayerOrientation = 'portrait' | 'landscape';
// Portrait keeps the legacy key (shared/synced with the editor's Phone tab).
// Landscape gets its own key so the editor flat-opts sync is never disturbed.
const PHONE_KEY = 'synapse:flow:settings:phone';
const PHONE_LANDSCAPE_KEY = 'synapse:flow:settings:phone:landscape';
const ORIENT_KEY = 'synapse:player:orientation';
const DEFAULT_PHONE_OPTS: PhoneOpts = { noteSpeed: 1, noteSize: 1, trackTilt: 0, camHeight: 0, camDist: 0 };

/** Load one orientation's opts. `seedUrl` lets portrait pick up QR query params
 *  (spd/sz/tilt/cy/cd) on first load; landscape just uses saved/default. */
function loadOptsFor(key: string, seedUrl: boolean): PhoneOpts {
  const sp = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const urlNum = (k: string) => { const v = parseFloat(sp.get(k) || ''); return Number.isFinite(v) ? v : undefined; };
  let stored: Partial<PhoneOpts> = {};
  try { stored = JSON.parse(localStorage.getItem(key) || '{}') || {}; } catch { /* ignore */ }
  const pick = (k: keyof PhoneOpts, urlKey: string) =>
    Number.isFinite(stored[k] as number) ? (stored[k] as number)
      : (seedUrl ? (urlNum(urlKey) ?? DEFAULT_PHONE_OPTS[k]) : DEFAULT_PHONE_OPTS[k]);
  return {
    noteSpeed: clampNum(pick('noteSpeed', 'spd'), 0.3, 3),
    noteSize: clampNum(pick('noteSize', 'sz'), 0.3, 3),
    trackTilt: clampNum(pick('trackTilt', 'tilt'), -30, 60),
    camHeight: clampNum(pick('camHeight', 'cy'), -6, 8),
    camDist: clampNum(pick('camDist', 'cd'), -6, 16),
  };
}
const loadPhoneOpts = (o: PlayerOrientation): PhoneOpts =>
  o === 'landscape' ? loadOptsFor(PHONE_LANDSCAPE_KEY, false) : loadOptsFor(PHONE_KEY, true);
function savePhoneOpts(o: PlayerOrientation, p: PhoneOpts): void {
  try { localStorage.setItem(o === 'landscape' ? PHONE_LANDSCAPE_KEY : PHONE_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}
function loadOrientation(): PlayerOrientation {
  try { return localStorage.getItem(ORIENT_KEY) === 'landscape' ? 'landscape' : 'portrait'; } catch { return 'portrait'; }
}
function saveOrientation(o: PlayerOrientation): void { try { localStorage.setItem(ORIENT_KEY, o); } catch { /* ignore */ } }

// Hit-sound on/off — a simple per-device preference (default on).
const HIT_SOUND_KEY = 'synapse:player:hitSound';
function loadHitSound(): boolean { try { return localStorage.getItem(HIT_SOUND_KEY) !== 'off'; } catch { return true; } }
function saveHitSound(on: boolean): void { try { localStorage.setItem(HIT_SOUND_KEY, on ? 'on' : 'off'); } catch { /* ignore */ } }

// Applies the camera pose (tilt + vertical pan) to the live camera so the gear
// panel sliders update the view immediately.
function CameraRig({ pos, targetY }: { pos: [number, number, number]; targetY: number }) {
  const { camera } = useThree();
  useEffect(() => {
    camera.position.set(pos[0], pos[1], pos[2]);
    camera.lookAt(CAM_TARGET[0], targetY, CAM_TARGET[2]);
  }, [camera, pos, targetY]);
  return null;
}

interface Props {
  audioUrl: string;
  title: string;
  rawChart: any;
  diff: any;
  bpm: number;
  onExit: () => void;
}
type Phase = 'ready' | 'playing' | 'done';

interface Spark { x: number; y: number; z: number; vx: number; vy: number; vz: number; life: number; color: THREE.Color; }

export default function PlayGame({ audioUrl, title, rawChart, diff, bpm, onExit }: Props) {
  const [phase, setPhase] = useState<Phase>('ready');
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [audioReady, setAudioReady] = useState(false);

  // Live gameplay material/visual settings (the user's saved values, or the
  // baked defaults). Read once — they don't change during a play session.
  const gp: FlowGameplaySettings = useFlowStore(s => s.gameplaySettings) ?? DEFAULT_FLOW_GAMEPLAY;

  // Orientation (portrait | landscape). Each orientation has its OWN set of the
  // view-tuning opts below, so the track can be framed independently for each.
  const [orientation, setOrientation] = useState<PlayerOrientation>(loadOrientation);
  const orientationRef = useRef(orientation);
  useEffect(() => { orientationRef.current = orientation; }, [orientation]);

  // Phone tuning for the ACTIVE orientation — adjustable in-game via the ⚙ panel
  // and persisted per-orientation (portrait seeded from QR params on first load).
  const [phone, setPhone] = useState<PhoneOpts>(() => loadPhoneOpts(orientation));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const updatePhone = useCallback((patch: Partial<PhoneOpts>) => {
    setPhone(prev => {
      const next = { ...prev, ...patch };
      savePhoneOpts(orientationRef.current, next);
      return next;
    });
  }, []);
  // Switching orientation loads that orientation's saved opts into the view.
  const chooseOrientation = useCallback((o: PlayerOrientation) => {
    setOrientation(o);
    orientationRef.current = o;
    saveOrientation(o);
    setPhone(loadPhoneOpts(o));
  }, []);

  // Adopt the server's global default once, but only if THIS device hasn't
  // personalized (no local override) — local always wins after that.
  useEffect(() => {
    let alive = true;
    let hasLocal = false;
    try { hasLocal = !!localStorage.getItem(orientation === 'landscape' ? PHONE_LANDSCAPE_KEY : PHONE_KEY); } catch { /* ignore */ }
    if (hasLocal) return;
    fetchGlobalDefaults().then(g => {
      if (!alive || !g) return;
      setPhone({
        noteSpeed: clampNum(g.noteSpeed, 0.3, 3),
        noteSize: clampNum(g.noteSize, 0.3, 3),
        trackTilt: clampNum(g.trackTilt, -30, 60),
        camHeight: clampNum((g as any).camHeight ?? 0, -6, 8),
        camDist: clampNum((g as any).camDist ?? 0, -6, 16),
      });
    });
    return () => { alive = false; };
  }, []);

  // Persist the current values as the global default for everyone (admin).
  const saveAsGlobal = useCallback(() => saveGlobalDefaults(phone), [phone]);

  // Hit-sound toggle (read via ref inside the tap handler to avoid stale closures).
  const [hitSound, setHitSound] = useState(loadHitSound);
  const hitSoundRef = useRef(hitSound);
  useEffect(() => { hitSoundRef.current = hitSound; }, [hitSound]);
  const toggleHitSound = useCallback(() => setHitSound(p => { const n = !p; saveHitSound(n); return n; }), []);
  const zSpeed = BASE_Z_SPEED * phone.noteSpeed;
  const lead = SPAWN_DIST / zSpeed;                 // = BASE_LEAD / noteSpeed
  const camPos = useMemo(() => {
    const base = cameraPosForTilt(phone.trackTilt, phone.camDist);
    return [base[0], base[1] + phone.camHeight, base[2]] as [number, number, number];
  }, [phone.trackTilt, phone.camHeight, phone.camDist]);
  const camTargetY = CAM_TARGET[1] + phone.camHeight;

  // Lane count for the chosen difficulty (3 or 4). Notes/pads keep their size;
  // only the distribution across the track changes.
  const lanes = laneCountOfDifficulty(diff);
  const notesRef = useRef<PlayNote[]>([]);
  const statsRef = useRef({ score: 0, combo: 0, maxCombo: 0, perfect: 0, good: 0, miss: 0 });
  // Sized to the max (4) so a 3-lane chart never indexes out of range.
  const padFlash = useRef<number[]>(new Array(PLAY_LANE_COUNT).fill(-1));
  const heldLane = useRef<boolean[]>(new Array(PLAY_LANE_COUNT).fill(false));
  const sparksRef = useRef<Spark[]>([]);
  const [hud, setHud] = useState({ score: 0, combo: 0, mult: 1, acc: 100 });
  const [results, setResults] = useState<typeof statsRef.current | null>(null);

  useEffect(() => {
    notesRef.current = notesFromDifficulty(rawChart, diff);
    let alive = true;
    audio.reset();
    if (!audioUrl) { setLoadErr('No audio in this beatmap.'); return; }
    audio.loadAudioBuffer(audioUrl)
      .then(() => { if (alive) setAudioReady(true); })
      .catch(e => { if (alive) setLoadErr(String(e?.message || e)); });
    return () => { alive = false; audio.reset(); };
  }, [rawChart, diff, audioUrl]);

  const mult = (combo: number) => Math.min(8, 1 + Math.floor(combo / 10));

  const spawnSparks = useCallback((lane: number, perfect: boolean) => {
    const col = new THREE.Color(gp.laneColors[lane]);
    const n = perfect ? 14 : 8;
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      sparksRef.current.push({
        x: laneXFor(lane, lanes), y: 0.15, z: 0,
        vx: Math.cos(a) * (0.6 + Math.random()), vy: 1.6 + Math.random() * 1.8,
        vz: Math.sin(a) * (0.6 + Math.random()) - 0.4, life: 1, color: col,
      });
    }
    if (sparksRef.current.length > 240) sparksRef.current.splice(0, sparksRef.current.length - 240);
  }, [gp.laneColors]);

  const judgeLane = useCallback((lane: number) => {
    const now = audio.getHeardPosition();
    padFlash.current[lane] = now;
    heldLane.current[lane] = true;
    const notes = notesRef.current;
    let best: PlayNote | null = null, bestDt = HIT_GOOD + 1;
    for (const n of notes) {
      if (n.lane !== lane || n.judged) continue;
      const dt = Math.abs(n.timeSec - now);
      if (dt < bestDt) { bestDt = dt; best = n; }
      if (n.timeSec - now > HIT_GOOD) break;
    }
    if (!best || bestDt > HIT_GOOD) return;
    const s = statsRef.current;
    if (bestDt <= HIT_PERFECT) { best.judged = 'perfect'; s.perfect++; s.score += 100 * mult(s.combo); }
    else { best.judged = 'good'; s.good++; s.score += 50 * mult(s.combo); }
    if (best.kind === 'trail') best.held = true;
    s.combo++; s.maxCombo = Math.max(s.maxCombo, s.combo);
    if (hitSoundRef.current) { try { playLaneHit(lane as any, { beat: 0, src: 'player' } as any); } catch { /* ignore */ } }
    tapHaptic(best.judged === 'perfect');
    spawnSparks(lane, best.judged === 'perfect');
  }, [spawnSparks]);

  const releaseLane = useCallback((lane: number) => { heldLane.current[lane] = false; }, []);

  const start = useCallback(() => {
    statsRef.current = { score: 0, combo: 0, maxCombo: 0, perfect: 0, good: 0, miss: 0 };
    notesRef.current.forEach(n => { n.judged = undefined; n.held = false; });
    sparksRef.current = [];
    initHaptics();  // create the hidden iOS haptic switch up front
    setPhase('playing');
    audio.seek(0); audio.play(0);
  }, []);

  const onEnd = useCallback(() => { setResults({ ...statsRef.current }); setPhase('done'); audio.pause(); }, []);

  return (
    <div style={root}>
      <Canvas dpr={[1, 2.5]} gl={{ antialias: true }} camera={{ position: camPos, fov: 58 }} onCreated={({ camera }) => camera.lookAt(CAM_TARGET[0], camTargetY, CAM_TARGET[2])}>
        <CameraRig pos={camPos} targetY={camTargetY} />
        <color attach="background" args={['#070512']} />
        {/* Pull the fog in so the far end of the track fades into the
            background — the highway reads shorter and distant notes are masked
            until they approach. */}
        <fog attach="fog" args={['#070512', 9, 22]} />
        {/* Editor light rig (FlowScene) so materials shade identically */}
        <ambientLight intensity={1.2} />
        <directionalLight position={[0, 8, 5]} intensity={0.9} color="#FFFFFF" />
        <pointLight position={[0, 6, -3]} intensity={1.4} color="#D946EF" />
        <pointLight position={[0, 3, 30]} intensity={1.2} color="#7C3AED" />
        <pointLight position={[0, 2, 10]} intensity={0.8} color="#FFFFFF" />
        <Highway gp={gp} lanes={lanes} padFlash={padFlash} heldLane={heldLane} />
        <ScrollingGrid bpm={bpm} playing={phase === 'playing'} zSpeed={zSpeed} />
        {phase === 'playing' && (
          <GameScene gp={gp} lanes={lanes} zSpeed={zSpeed} lead={lead} noteSizeMul={phone.noteSize}
            notesRef={notesRef} statsRef={statsRef} padFlash={padFlash} heldLane={heldLane}
            sparksRef={sparksRef} setHud={setHud} onEnd={onEnd} />
        )}
      </Canvas>

      {/* lane tap zones (screen-left = lane 0) */}
      {phase === 'playing' && (
        <div style={laneZones}>
          {Array.from({ length: lanes }).map((_, i) => (
            <div key={i} style={{ flex: 1 }}
              onPointerDown={e => { e.preventDefault(); judgeLane(i); }}
              onPointerUp={() => releaseLane(i)} onPointerCancel={() => releaseLane(i)} />
          ))}
        </div>
      )}

      {phase === 'playing' && (
        <div style={hudWrap}>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{hud.score.toLocaleString()}</div>
          <div style={{ textAlign: 'center', fontWeight: 700 }}><b style={{ fontSize: 26 }}>{hud.combo}</b> <span style={{ color: '#22D3EE' }}>×{hud.mult}</span></div>
          <div style={{ fontSize: 16, color: '#9B8CFF' }}>{hud.acc.toFixed(1)}%</div>
        </div>
      )}

      {/* ⚙ Settings — top-right, available on every screen */}
      <button style={gearBtn} onClick={() => setSettingsOpen(o => !o)} aria-label="Settings">⚙</button>
      {settingsOpen && (
        <PhonePanel phone={phone} orientation={orientation} onChange={updatePhone} hitSound={hitSound} onToggleHitSound={toggleHitSound}
          onSaveGlobal={saveAsGlobal} onClose={() => setSettingsOpen(false)} />
      )}

      {phase === 'ready' && (
        <div style={overlay}>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{title}</div>
          {/* Orientation picker — each mode keeps its own track-view settings. */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div style={{ display: 'flex', gap: 10 }}>
              {(['portrait', 'landscape'] as const).map(o => {
                const active = orientation === o;
                return (
                  <button
                    key={o}
                    onClick={() => chooseOrientation(o)}
                    style={{
                      padding: '8px 18px',
                      borderRadius: 10,
                      fontWeight: 700,
                      textTransform: 'capitalize',
                      cursor: 'pointer',
                      border: `1px solid ${active ? 'rgba(34,211,238,0.6)' : 'rgba(120,80,220,0.3)'}`,
                      background: active ? 'rgba(34,211,238,0.15)' : 'rgba(15,15,30,0.6)',
                      color: active ? '#22D3EE' : '#C4B5FD',
                    }}
                  >
                    {o === 'portrait' ? '▯ Portrait' : '▭ Landscape'}
                  </button>
                );
              })}
            </div>
            {orientation === 'landscape' && (
              <div style={{ fontSize: 11, color: '#9B8CFF', opacity: 0.8 }}>Rotate your device sideways</div>
            )}
          </div>
          {loadErr && <div style={{ color: '#FCA5A5', maxWidth: 320, textAlign: 'center' }}>{loadErr}</div>}
          {!loadErr && <button style={bigBtn} disabled={!audioReady} onClick={start}>{audioReady ? '▶ Tap to play' : 'Loading…'}</button>}
          <button style={textBtn} onClick={onExit}>‹ Back</button>
        </div>
      )}
      {phase === 'done' && results && (
        <div style={overlay}>
          <div style={{ fontSize: 24, fontWeight: 800 }}>Results</div>
          <div style={{ fontSize: 40, fontWeight: 900, color: '#C4B5FD' }}>{results.score.toLocaleString()}</div>
          <div style={resRow}>Max combo <b>{results.maxCombo}</b></div>
          <div style={resRow}>Accuracy <b>{accuracy(results).toFixed(1)}%</b></div>
          <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
            <span style={{ color: '#22D3EE' }}>Perfect {results.perfect}</span>
            <span style={{ color: '#EAB308' }}>Good {results.good}</span>
            <span style={{ color: '#EF4444' }}>Miss {results.miss}</span>
          </div>
          <button style={bigBtn} onClick={start}>↻ Retry</button>
          <button style={textBtn} onClick={onExit}>‹ Songs</button>
        </div>
      )}
    </div>
  );
}

const TRACK_LEN = SPAWN_DIST + 6;   // constant: spawn distance is speed-invariant

// ── Note.fbx → one normalised, instanceable BufferGeometry ───────────────────
// A box the size of the editor note head — used while the FBX loads and as a
// permanent fallback if the model can't be fetched/parsed (e.g. on a flaky
// mobile connection), so the player NEVER blank-screens over a missing asset.
function fallbackNoteGeometry(noteScale: number): THREE.BufferGeometry {
  const s = Math.max(0.1, noteScale);
  return new THREE.BoxGeometry(LANE_W * 0.42 * s, 0.16 * s, NOTE_LENGTH * 0.95 * s);
}

// Bakes each child mesh's transform, merges to a single position buffer,
// recomputes smooth normals (FBX ships hard split-normals), centres on the
// origin and scales the longest axis to NOTE_LENGTH·0.95·noteScale — exactly
// what the editor's NoteModel does per-instance, but once, so we can instance.
function buildNoteGeometry(fbx: THREE.Group, noteScale: number): THREE.BufferGeometry {
  fbx.updateMatrixWorld(true);
  const geos: THREE.BufferGeometry[] = [];
  fbx.traverse(child => {
    const m = child as THREE.Mesh;
    if (!m.isMesh || !m.geometry) return;
    let g = m.geometry.clone();
    g.applyMatrix4(m.matrixWorld);
    if (g.index) g = g.toNonIndexed();
    const pos = g.getAttribute('position');
    const ng = new THREE.BufferGeometry();
    ng.setAttribute('position', (pos as THREE.BufferAttribute).clone());
    geos.push(ng);
  });
  if (geos.length === 0) return fallbackNoteGeometry(noteScale);
  const merged = (geos.length === 1 ? geos[0] : mergeBufferGeometries(geos, false)) || geos[0];
  merged.computeVertexNormals();
  merged.computeBoundingBox();
  const box = merged.boundingBox!;
  const size = new THREE.Vector3(); box.getSize(size);
  const center = new THREE.Vector3(); box.getCenter(center);
  merged.translate(-center.x, -center.y, -center.z);
  const maxDim = Math.max(size.x, size.y, size.z, 0.001);
  const s = (NOTE_LENGTH * 0.95 * Math.max(0.1, noteScale)) / maxDim;
  merged.scale(s, s, s);
  return merged;
}

// Loads Note.fbx imperatively (NOT drei's useFBX, which THROWS on a failed
// load and can't be caught by Suspense → blank screen). On any error we keep
// the box fallback so gameplay always renders.
function useNoteGeometry(noteScale: number): THREE.BufferGeometry {
  const [geom, setGeom] = useState<THREE.BufferGeometry>(() => fallbackNoteGeometry(noteScale));
  useEffect(() => {
    let alive = true;
    const fallback = fallbackNoteGeometry(noteScale);
    setGeom(fallback);
    try {
      new FBXLoader().load(
        NOTE_FBX_PATH,
        (fbx) => { if (!alive) return; try { setGeom(buildNoteGeometry(fbx as unknown as THREE.Group, noteScale)); } catch { /* keep fallback */ } },
        undefined,
        () => { /* network/parse error → keep fallback box */ },
      );
    } catch { /* keep fallback */ }
    return () => { alive = false; };
  }, [noteScale]);
  return geom;
}

// Editor trail ribbon shader (FlowScene TrailTube): per-vertex lane-colour
// gradient + a highlight that sweeps along the trail as it's consumed.
function makeTrailMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    vertexColors: true,
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
    uniforms: {
      uTime: { value: 0 },
      uPlaying: { value: 1 },
      uBeatProgress: { value: -1 },
    },
    vertexShader: /* glsl */`
      varying vec3 vColor;
      varying vec2 vUv;
      void main() { vColor = color; vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
    `,
    fragmentShader: /* glsl */`
      uniform float uTime; uniform float uPlaying; uniform float uBeatProgress;
      varying vec3 vColor;
      varying vec2 vUv;
      void main() {
        float normY = vUv.y;
        float du = vUv.x * 2.0 - 1.0;
        float edgeFade = 1.0 - smoothstep(0.80, 1.0, abs(du));
        float posN;
        if (uPlaying > 0.5) { posN = uBeatProgress; if (posN < 0.0 || posN > 1.0) posN = -99.0; }
        else { posN = mod(uTime * 0.40, 1.0); }
        float d = abs(normY - posN); d = min(d, 1.0 - d);
        float band = exp(-d * d * 55.0);
        float core = exp(-d * d * 380.0);
        float oval = exp(-du * du * 1.8);
        vec3 col = vColor * 0.72;
        col += vColor * band * 0.55 * oval;
        col += vec3(1.0) * core * 0.90 * oval;
        gl_FragColor = vec4(col, 0.88 * edgeFade);
      }
    `,
  });
}

// Build a flat curved ribbon (Catmull-Rom through the trail's waypoints) in
// ABSOLUTE highway space: z = timeSec·zSpeed. A scrolling parent group then
// shifts it by -now·zSpeed each frame, so the geometry is built only once.
// Mirrors the editor's TrailTube geometry (gradient by lane, flat on floor).
function buildTrailGeometry(points: { timeSec: number; lane: number }[], trailWidth: number, laneColors: readonly string[], zSpeed: number, lanes: number): THREE.BufferGeometry {
  const base = points.map(p => new THREE.Vector3(laneXFor(p.lane, lanes), TRAIL_Y, p.timeSec * zSpeed));
  const baseCols = points.map(p => new THREE.Color(laneColors[p.lane] ?? '#ffffff'));
  const n = base.length;
  // Overshoot each end along its tangent so the note head (which sits at the
  // first waypoint) always rests ON a continuous ribbon instead of at its bare
  // tip — kills the visual gap between the note and the rail.
  const OVER = Math.max(0.6, zSpeed * 0.07);
  const startExt = base[0].clone().add(base[0].clone().sub(base[1]).setLength(OVER));
  const endExt = base[n - 1].clone().add(base[n - 1].clone().sub(base[n - 2]).setLength(OVER));
  const ctrl = [startExt, ...base, endExt];
  const cols = [baseCols[0].clone(), ...baseCols, baseCols[n - 1].clone()];

  const curve = new THREE.CatmullRomCurve3(ctrl, false, 'centripetal');
  const samples = Math.max(48, (ctrl.length - 1) * 24);
  const halfW = Math.max(0.05, trailWidth) / 2;

  const position: number[] = [], normal: number[] = [], color: number[] = [], uv: number[] = [], index: number[] = [];
  for (let i = 0; i <= samples; i++) {
    const u = i / samples;
    const pt = curve.getPointAt(u);
    const tg = curve.getTangentAt(u);
    const rx = -tg.z, rz = tg.x;
    const rLen = Math.sqrt(rx * rx + rz * rz);
    const nrx = rLen > 1e-4 ? rx / rLen : 1, nrz = rLen > 1e-4 ? rz / rLen : 0;
    const lx = pt.x - nrx * halfW, lz = pt.z - nrz * halfW;
    const vx = pt.x + nrx * halfW, vz = pt.z + nrz * halfW;
    const segF = u * (ctrl.length - 1);
    const segIdx = Math.min(Math.floor(segF), ctrl.length - 2);
    const c = cols[segIdx].clone().lerp(cols[segIdx + 1], segF - segIdx);
    position.push(lx, TRAIL_Y, lz, vx, TRAIL_Y, vz);
    normal.push(0, 1, 0, 0, 1, 0);
    color.push(c.r, c.g, c.b, c.r, c.g, c.b);
    uv.push(0, u, 1, u);
  }
  for (let i = 0; i < samples; i++) {
    const a = i * 2, b = i * 2 + 1, c2 = i * 2 + 2, d = i * 2 + 3;
    index.push(a, b, c2, b, d, c2);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(position, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(normal, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(color, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(index);
  return g;
}

// ── Static highway (floor, side rails, lane dividers, pill trigger pads) ─────
function Highway({ gp, lanes, padFlash, heldLane }: {
  gp: FlowGameplaySettings;
  lanes: number;
  padFlash: React.MutableRefObject<number[]>;
  heldLane: React.MutableRefObject<boolean[]>;
}) {
  const laneSpacing = TRACK_W / lanes;
  const dividers = [];
  for (let i = 0; i <= lanes; i++) dividers.push(-TRACK_W / 2 + i * laneSpacing);

  // Pill-OUTLINE trigger pads at the strike line: a 4px line tracing a stadium
  // shape per lane in its colour. Brightens (opacity + thicker) while tapped.
  // Pad SIZE is fixed (LANE_W) so it doesn't grow when lanes are wider.
  const padPts = useMemo(() => pillOutlinePoints(LANE_W * 0.86, 0.5), []);
  const padRefs = useRef<(any | null)[]>([]);
  useFrame(() => {
    const now = audio.getHeardPosition();
    for (let lane = 0; lane < lanes; lane++) {
      const ln = padRefs.current[lane]; if (!ln || !ln.material) continue;
      const lit = now - padFlash.current[lane] < 0.12 || heldLane.current[lane];
      ln.material.opacity = lit ? 1.0 : 0.7;
      ln.material.linewidth = lit ? 6 : 4;
    }
  });

  return (
    <group>
      {/* floor — unlit very-dark violet, matching the editor deck (#0a0418) */}
      <mesh position={[0, -0.02, TRACK_LEN / 2]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[TRACK_W, TRACK_LEN]} />
        <meshBasicMaterial color="#0a0418" />
      </mesh>
      {/* glowing side rails */}
      {[-TRACK_W / 2, TRACK_W / 2].map((x, i) => (
        <mesh key={i} position={[x, 0.04, TRACK_LEN / 2]}>
          <boxGeometry args={[0.06, 0.08, TRACK_LEN]} />
          <meshStandardMaterial color="#7C3AED" emissive="#7C3AED" emissiveIntensity={1.2} />
        </mesh>
      ))}
      {/* lane dividers */}
      {dividers.slice(1, lanes).map((x, i) => (
        <mesh key={i} position={[x, 0.005, TRACK_LEN / 2]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[0.03, TRACK_LEN]} />
          <meshBasicMaterial color="#5B2EC5" transparent opacity={0.45} />
        </mesh>
      ))}
      {/* pill-outline trigger pads at the strike line (4px line per lane) */}
      {Array.from({ length: lanes }).map((_, lane) => (
        <group key={lane} position={[laneXFor(lane, lanes), 0.045, 0]}>
          <Line
            ref={(el: any) => { padRefs.current[lane] = el; }}
            points={padPts}
            color={gp.laneColors[lane]}
            lineWidth={4}
            transparent
            opacity={0.7}
            toneMapped={false}
          />
        </group>
      ))}
    </group>
  );
}

// ── Scrolling beat-grid lines (gives the highway depth + motion) ─────────────
function ScrollingGrid({ bpm, playing, zSpeed }: { bpm: number; playing: boolean; zSpeed: number }) {
  const groupRef = useRef<THREE.Group>(null);
  const spacing = zSpeed * (60 / Math.max(60, bpm));   // world units per beat
  const COUNT = Math.ceil(TRACK_LEN / spacing) + 2;
  useFrame(() => {
    const g = groupRef.current; if (!g) return;
    const now = playing ? audio.getHeardPosition() : 0;
    g.position.z = -((now * zSpeed) % spacing);
  });
  return (
    <group ref={groupRef}>
      {Array.from({ length: COUNT }).map((_, i) => (
        <mesh key={i} position={[0, 0.004, i * spacing]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[TRACK_W, 0.04]} />
          <meshBasicMaterial color="#A78BFA" transparent opacity={0.4} />
        </mesh>
      ))}
    </group>
  );
}

// ── Dynamic scene: notes (instanced FBX), trails (shader), sparks, loop ──────
function GameScene({ gp, lanes, zSpeed, lead, noteSizeMul, notesRef, statsRef, padFlash, heldLane, sparksRef, setHud, onEnd }: any) {
  const notes: PlayNote[] = notesRef.current;
  const geometry = useNoteGeometry(gp.noteScale * noteSizeMul);

  // One InstancedMesh per lane (per-lane emissive glow); stable instance slot
  // = index within the lane bucket.
  const lanesIdx = useMemo(() => {
    const buckets: number[][] = Array.from({ length: lanes }, () => []);
    notes.forEach((n, i) => { if (n.lane >= 0 && n.lane < lanes) buckets[n.lane].push(i); });
    return buckets;
  }, [notes, lanes]);

  const instRefs = useRef<(THREE.InstancedMesh | null)[]>([]);
  const trailGroupRef = useRef<THREE.Group>(null);
  // Per-trail curved ribbon geometry + material, built once. Geometry is in
  // absolute highway space; the parent group scrolls it.
  const trails = useMemo(() => {
    return notes
      .map((n, i) => ({ n, i }))
      .filter(({ n }) => n.kind === 'trail' && n.points && n.points.length >= 2)
      .map(({ n, i }) => ({
        i,
        id: n.id,
        note: n,
        geom: buildTrailGeometry(n.points!, gp.trailWidth, gp.laneColors, zSpeed, lanes),
        mat: makeTrailMaterial(),
      }));
  }, [notes, gp.trailWidth, gp.laneColors, zSpeed, lanes]);
  useEffect(() => () => { trails.forEach(t => { t.geom.dispose(); t.mat.dispose(); }); }, [trails]);

  const sparkMesh = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const hudTick = useRef(0);

  useFrame((state, dt) => {
    const now = audio.getHeardPosition();
    const s = statsRef.current;
    const t = state.clock.elapsedTime;

    // auto-miss
    for (const n of notes) if (!n.judged && now - n.timeSec > HIT_GOOD) { n.judged = 'miss'; s.miss++; s.combo = 0; }

    // note heads (instanced, per lane)
    for (let lane = 0; lane < lanes; lane++) {
      const inst = instRefs.current[lane]; if (!inst) continue;
      const bucket = lanesIdx[lane];
      for (let j = 0; j < bucket.length; j++) {
        const n = notes[bucket[j]];
        const p = (n.timeSec - now) / lead;
        const visible = p >= -0.06 && p <= 1 && n.judged !== 'perfect' && n.judged !== 'good';
        if (visible) {
          dummy.position.set(laneXFor(lane, lanes), NOTE_Y, (n.timeSec - now) * zSpeed);
          dummy.rotation.set(0, 0, 0);
          dummy.scale.setScalar(1);
        } else {
          dummy.scale.setScalar(0);
        }
        dummy.updateMatrix();
        inst.setMatrixAt(j, dummy.matrix);
      }
      inst.instanceMatrix.needsUpdate = true;
    }

    // trails — geometry is in absolute highway space, so the whole group just
    // scrolls toward the camera. Per-trail we advance the highlight sweep.
    if (trailGroupRef.current) trailGroupRef.current.position.z = -now * zSpeed;
    for (const tr of trails) {
      tr.mat.uniforms.uTime.value = t;
      tr.mat.uniforms.uBeatProgress.value =
        (now - tr.note.timeSec) / Math.max(0.001, tr.note.endSec - tr.note.timeSec);
    }

    // (pad glow lives in Highway now — the pill outlines brighten on hit)

    // sparks (instanced)
    const sp = sparksRef.current;
    for (let i = sp.length - 1; i >= 0; i--) {
      const k = sp[i];
      k.life -= dt * 1.6; if (k.life <= 0) { sp.splice(i, 1); continue; }
      k.vy -= dt * 5; k.x += k.vx * dt; k.y += k.vy * dt; k.z += k.vz * dt;
    }
    const im = sparkMesh.current;
    if (im) {
      for (let i = 0; i < 240; i++) {
        const k = sp[i];
        if (k) { dummy.position.set(k.x, k.y, k.z); dummy.rotation.set(0, 0, 0); dummy.scale.setScalar(0.06 * k.life + 0.02); }
        else dummy.scale.setScalar(0);
        dummy.updateMatrix(); im.setMatrixAt(i, dummy.matrix);
        if (k) im.setColorAt(i, k.color);
      }
      im.instanceMatrix.needsUpdate = true;
      if (im.instanceColor) im.instanceColor.needsUpdate = true;
    }

    // HUD (throttle)
    if (++hudTick.current % 4 === 0) setHud({ score: s.score, combo: s.combo, mult: Math.min(8, 1 + Math.floor(s.combo / 10)), acc: accuracy(s) });

    // end
    const last = notes.length ? notes[notes.length - 1].endSec : 0;
    if (now > last + 1.2 || (!audio.isPlaying() && now > 0.5 && now >= audio.getDuration() - 0.05)) onEnd();
  });

  return (
    <group>
      {/* note heads — one instanced FBX mesh per lane */}
      {Array.from({ length: lanes }).map((_, lane) => (
        <instancedMesh
          key={lane}
          ref={el => { instRefs.current[lane] = el; }}
          args={[geometry, undefined as any, Math.max(1, lanesIdx[lane].length)]}
          frustumCulled={false}
        >
          <meshStandardMaterial
            color={gp.laneColors[lane]} emissive={gp.laneColors[lane]}
            emissiveIntensity={Math.max(gp.noteEmissive, 0.6)}
            metalness={gp.noteMetalness} roughness={gp.noteRoughness}
          />
        </instancedMesh>
      ))}

      {/* trails — curved shader ribbons (Catmull-Rom through waypoints) built
          in absolute highway space; this group scrolls them toward the camera */}
      <group ref={trailGroupRef}>
        {trails.map(t => (
          <mesh key={t.id} geometry={t.geom} material={t.mat} />
        ))}
      </group>

      <instancedMesh ref={sparkMesh} args={[undefined as any, undefined as any, 240]}>
        <boxGeometry args={[1, 1, 1]} />
        <meshBasicMaterial toneMapped={false} />
      </instancedMesh>
    </group>
  );
}

function accuracy(s: { perfect: number; good: number; miss: number }): number {
  const t = s.perfect + s.good + s.miss;
  return t ? ((s.perfect + s.good * 0.5) / t) * 100 : 100;
}

// ── In-game settings panel ───────────────────────────────────────────────────
function PSlider({ label, value, min, max, step, onChange, fmt }: {
  label: string; value: number; min: number; max: number; step: number;
  onChange: (v: number) => void; fmt: (v: number) => string;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 8 }}>
        <span>{label}</span>
        <span style={{ color: '#9B8CFF', fontWeight: 700 }}>{fmt(value)}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        style={{ width: '100%', accentColor: '#a855f7', height: 28 }}
      />
    </div>
  );
}

function PhonePanel({ phone, orientation, onChange, hitSound, onToggleHitSound, onSaveGlobal, onClose }: {
  phone: PhoneOpts; orientation: PlayerOrientation; onChange: (p: Partial<PhoneOpts>) => void;
  hitSound: boolean; onToggleHitSound: () => void;
  onSaveGlobal: () => Promise<boolean>; onClose: () => void;
}) {
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const saveGlobal = async () => {
    setStatus('saving');
    const ok = await onSaveGlobal();
    setStatus(ok ? 'saved' : 'error');
    setTimeout(() => setStatus('idle'), 2600);
  };
  return (
    <>
      <div style={panelBackdrop} onClick={onClose} />
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(167,139,250,0.5)', margin: '0 auto 12px' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>
            Settings <span style={{ fontSize: 12, fontWeight: 600, color: '#22D3EE', textTransform: 'capitalize' }}>· {orientation}</span>
          </div>
          <button style={panelClose} onClick={onClose}>✕</button>
        </div>
        <PSlider label="Note Speed" value={phone.noteSpeed} min={0.5} max={2} step={0.05}
          onChange={v => onChange({ noteSpeed: v })} fmt={v => `${v.toFixed(2)}×`} />
        <PSlider label="Note Size" value={phone.noteSize} min={0.5} max={2} step={0.05}
          onChange={v => onChange({ noteSize: v })} fmt={v => `${Math.round(v * 100)}%`} />
        <PSlider label="Track Tilt" value={phone.trackTilt} min={-12} max={38} step={1}
          onChange={v => onChange({ trackTilt: Math.round(v) })} fmt={v => `${v > 0 ? '+' : ''}${Math.round(v)}°`} />
        <PSlider label="Camera Height" value={phone.camHeight} min={-4} max={6} step={0.25}
          onChange={v => onChange({ camHeight: v })} fmt={v => `${v > 0 ? '+' : ''}${v.toFixed(2)}`} />
        <PSlider label="Camera Distance" value={phone.camDist} min={-4} max={14} step={0.5}
          onChange={v => onChange({ camDist: v })} fmt={v => `${v > 0 ? '+' : ''}${v.toFixed(1)}`} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '2px 0 14px' }}>
          <span style={{ fontSize: 14 }}>Hit Sounds</span>
          <button style={hitSound ? toggleOn : toggleOff} onClick={onToggleHitSound}>
            {hitSound ? 'On' : 'Off'}
          </button>
        </div>
        <div style={{ fontSize: 11, color: '#7d75a8', lineHeight: 1.5, margin: '4px 0 16px' }}>
          Saved on this device automatically.
        </div>
        <button style={saveGlobalBtn} onClick={saveGlobal} disabled={status === 'saving'}>
          {status === 'saving' ? 'Saving…'
            : status === 'saved' ? '✓ Saved for everyone'
            : status === 'error' ? '✕ Backend unavailable'
            : '☁ Save as default for everyone'}
        </button>
        <div style={{ fontSize: 10, color: '#7d75a8', lineHeight: 1.5, marginTop: 8 }}>
          Makes these the global default for all players (needs the backend).
        </div>
      </div>
    </>
  );
}

// ── styles ───────────────────────────────────────────────────────────────────
const root: React.CSSProperties = { position: 'fixed', inset: 0, background: '#06060c', overflow: 'hidden', touchAction: 'none' };
const laneZones: React.CSSProperties = { position: 'absolute', left: 0, right: 0, bottom: 0, height: '55%', display: 'flex' };
const hudWrap: React.CSSProperties = { position: 'absolute', top: 10, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', padding: '0 18px', color: color.ink, pointerEvents: 'none', fontFamily: "'Montserrat',sans-serif" };
const overlay: React.CSSProperties = { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, background: 'rgba(6,6,12,0.72)', color: color.ink, fontFamily: "'Montserrat',sans-serif" };
const bigBtn: React.CSSProperties = { background: 'linear-gradient(135deg,#7c3aed,#22d3ee)', color: '#fff', border: 'none', borderRadius: 14, padding: '16px 34px', fontSize: 18, fontWeight: 800, cursor: 'pointer' };
const gearBtn: React.CSSProperties = {
  position: 'absolute', top: 10, right: 12, zIndex: 30,
  width: 40, height: 40, borderRadius: 10, cursor: 'pointer',
  background: 'rgba(20,16,40,0.7)', border: '1px solid rgba(167,139,250,0.4)',
  color: '#E9D5FF', fontSize: 20, lineHeight: '38px', textAlign: 'center', padding: 0,
};
const panelBackdrop: React.CSSProperties = { position: 'absolute', inset: 0, zIndex: 31, background: 'rgba(0,0,0,0.18)' };
// Top sheet — anchored to the top, ~half the screen, so the lower highway
// (strike line, pads, incoming notes) stays visible while you tweak live.
const panel: React.CSSProperties = {
  position: 'absolute', left: 0, right: 0, top: 0, zIndex: 32,
  height: 'auto', maxHeight: '52vh', padding: '16px 20px 16px', overflowY: 'auto',
  background: 'linear-gradient(0deg,#150c28,#0a0a14)', borderBottom: '1px solid rgba(167,139,250,0.35)',
  borderRadius: '0 0 18px 18px',
  color: color.ink, fontFamily: "'Montserrat',sans-serif",
  boxShadow: '0 14px 44px rgba(0,0,0,0.55)',
};
const panelClose: React.CSSProperties = {
  background: 'rgba(124,58,237,0.18)', border: '1px solid rgba(167,139,250,0.4)',
  color: '#E9D5FF', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', fontSize: 14, padding: 0,
};
const toggleBase: React.CSSProperties = {
  minWidth: 58, padding: '7px 14px', borderRadius: 9, cursor: 'pointer',
  fontSize: 13, fontWeight: 800, fontFamily: "'Montserrat',sans-serif",
};
const toggleOn: React.CSSProperties = {
  ...toggleBase, background: 'linear-gradient(135deg,#7c3aed,#22d3ee)', color: '#fff', border: 'none',
};
const toggleOff: React.CSSProperties = {
  ...toggleBase, background: 'rgba(20,16,40,0.7)', color: '#9B8CFF', border: '1px solid rgba(167,139,250,0.4)',
};
const saveGlobalBtn: React.CSSProperties = {
  width: '100%', padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
  background: 'linear-gradient(135deg,#7c3aed,#22d3ee)', color: '#fff', border: 'none',
  fontSize: 14, fontWeight: 800, fontFamily: "'Montserrat',sans-serif",
};
const textBtn: React.CSSProperties = { background: 'none', border: 'none', color: '#9B8CFF', fontSize: 15, cursor: 'pointer', padding: 8 };
const resRow: React.CSSProperties = { fontSize: 16, color: '#C9C2EA' };
